import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Complete Profile Page
 * Collects name and optional email after OTP registration
 * UI styled to match Foodwinc design
 */
const CompleteProfile = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, completeProfile, refreshUser } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if not logged in or profile already complete
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (user.profileCompleted && user.name) {
        navigate('/');
      }
    }
  }, [user, authLoading, navigate]);

  // Pre-fill with existing data if available
  useEffect(() => {
    if (user) {
      if (user.name) setName(user.name);
      if (user.email) setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || name.trim().length < 2) {
      setError('Please enter your name (minimum 2 characters)');
      return;
    }

    // Basic email validation if provided
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await completeProfile({
        name: name.trim(),
        email: email.trim() || undefined
      });

      // Refresh user data
      await refreshUser();

      // Navigate to home
      navigate('/');
    } catch (err) {
      console.error('Complete profile error:', err);
      setError(err.response?.data?.message || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get role display name
  const getRoleDisplayName = () => {
    if (!user) return '';
    switch (user.role) {
      case 'restaurant': return 'Restaurant Partner';
      case 'delivery': return 'Delivery Partner';
      case 'admin': return 'Administrator';
      default: return 'Customer';
    }
  };

  // Get role icon
  const getRoleIcon = () => {
    if (!user) return '';
    switch (user.role) {
      case 'restaurant': return '🍽️';
      case 'delivery': return '🚴';
      case 'admin': return '👨‍💼';
      default: return '👤';
    }
  };

  if (authLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div style={styles.page}>
      {/* Navigation */}
      <nav style={styles.navbar}>
        <div style={styles.navContainer}>
          <Link to="/" style={styles.logo}>
            <div style={styles.logoIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24 }}>
                <path d="M3 11h18M3 11a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2M3 11v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6M7 11V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4"/>
                <line x1="12" y1="15" x2="12" y2="15.01"/>
              </svg>
            </div>
            <div style={styles.logoText}>Food<span style={styles.logoAccent}>Winc</span></div>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.mainContainer}>
        <div style={styles.profileCard}>
          <div style={styles.profileHeader}>
            <div style={styles.avatar}>
              <span style={styles.avatarText}>{name ? name.charAt(0).toUpperCase() : '?'}</span>
            </div>
            <h1 style={styles.title}>Complete Your Profile</h1>
            <div style={styles.roleBadge}>
              <span>{getRoleIcon()} {getRoleDisplayName()}</span>
            </div>
            <p style={styles.phoneNumber}>+91 {user.phone?.replace(/(\d{5})(\d{5})/, '$1 $2')}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Full Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                disabled={loading}
                autoFocus
                required
                style={styles.formInput}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Email (Optional)</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={loading}
                style={styles.formInput}
              />
              <p style={styles.hint}>Used for order updates and receipts</p>
            </div>

            {error && <div style={styles.alertError}>{error}</div>}

            <button
              type="submit"
              style={{
                ...styles.submitBtn,
                ...(loading || !name.trim() ? styles.submitBtnDisabled : {})
              }}
              disabled={loading || !name.trim()}
            >
              {loading ? (
                <>
                  <span style={styles.btnSpinner}></span>
                  <span>Saving...</span>
                </>
              ) : (
                <span>Continue</span>
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

// Styles matching Foodwinc design
const styles = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    lineHeight: 1.6,
    color: '#1a1a2e',
    background: 'linear-gradient(135deg, #fef7f7 0%, #fff5f5 50%, #fef2f2 100%)',
    minHeight: '100vh',
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #fef7f7 0%, #fff5f5 50%, #fef2f2 100%)',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255, 105, 97, 0.2)',
    borderTopColor: '#FF6961',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  // Navbar
  navbar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.08)',
    padding: '16px 0',
  },
  navContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
  },
  logoIcon: {
    width: 44,
    height: 44,
    background: 'linear-gradient(135deg, #FF6961 0%, #D2625E 100%)',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  logoText: {
    fontSize: 24,
    fontWeight: 800,
    color: '#1a1a2e',
  },
  logoAccent: {
    color: '#FF6961',
  },
  // Main Container
  mainContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '100px 24px 40px',
  },
  profileCard: {
    background: 'white',
    borderRadius: 24,
    padding: '48px 40px',
    maxWidth: 440,
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
  },
  profileHeader: {
    textAlign: 'center',
    marginBottom: 32,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #FF6961 0%, #D2625E 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    boxShadow: '0 8px 24px rgba(255, 105, 97, 0.3)',
  },
  avatarText: {
    color: 'white',
    fontSize: 32,
    fontWeight: 600,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 12px',
  },
  roleBadge: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #FF6961 0%, #D2625E 100%)',
    color: 'white',
    padding: '6px 16px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 8,
  },
  phoneNumber: {
    color: '#64748b',
    fontSize: 14,
    margin: 0,
    fontWeight: 500,
  },
  formGroup: {
    marginBottom: 20,
  },
  formLabel: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: 8,
  },
  formInput: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: 12,
    fontSize: 15,
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
    background: '#f8fafc',
    outline: 'none',
    boxSizing: 'border-box',
  },
  hint: {
    fontSize: 12,
    color: '#94a3b8',
    margin: '8px 0 0',
  },
  alertError: {
    padding: '14px 16px',
    borderRadius: 12,
    marginBottom: 20,
    fontSize: 14,
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    textAlign: 'center',
  },
  submitBtn: {
    width: '100%',
    padding: 16,
    background: 'linear-gradient(135deg, #FF6961 0%, #D2625E 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(255, 105, 97, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  submitBtnDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  btnSpinner: {
    width: 20,
    height: 20,
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  input:focus {
    border-color: #FF6961 !important;
    background: white !important;
    box-shadow: 0 0 0 4px rgba(255, 105, 97, 0.1) !important;
  }

  input:disabled {
    background: #f1f5f9 !important;
    cursor: not-allowed;
  }
`;
if (!document.querySelector('style[data-complete-profile]')) {
  styleSheet.setAttribute('data-complete-profile', 'true');
  document.head.appendChild(styleSheet);
}

export default CompleteProfile;
